import type { TemperatureBand } from "@ui/utils/temperature-bands";
import type { InstantSearchProps } from "react-instantsearch";
import {
  LEGACY_SEARCH_PARAM_KEYS,
  parseCanonicalList,
  serializeCanonicalList,
  splitUrl,
  WHEN_PARAM_KEYS,
  WHERE_PARAM_KEYS,
} from "@/lib/search-params";
import {
  BUDGET_MAX_CENTS,
  BUDGET_MIN_CENTS,
  type KoppenMainClass,
  LANGUAGE_OPTIONS,
  type LanguageCode,
  TEMPERATURE_BANDS,
  UTC_OFFSETS,
  type UtcOffset,
} from "./filter-values";

type UiState = NonNullable<InstantSearchProps["initialUiState"]>;

/**
 * /destinations URL grammar for the filter panel and sort (Where/When ride the
 * shared grammar in `lib/search-params`):
 *
 *   ?budget=100000-450000&temp=warm&climate=tropical&timezone=-1,0,1&languages=en,pt&sort=price_asc
 *
 * `budget` is a `min-max` pair in USD cents, the same unit the range
 * refinement and the scoring payload carry, so a shared link and the wire
 * cannot disagree by a rounding — and the link means the same thing whatever
 * display currency the viewer has picked.
 *
 * Unlike programs/marketplace, the five filter lists are page-local state (see
 * DestinationsFiltersAdapter), so they parse into `useDestinationFilters`
 * initials and serialize from that state — never through InstantSearch uiState.
 * Only `sort` maps to uiState.
 */

const BUDGET_PARAM = "budget";
const TEMP_PARAM = "temp";
const CLIMATE_PARAM = "climate";
const TIMEZONE_PARAM = "timezone";
const LANGUAGES_PARAM = "languages";
const SORT_PARAM = "sort";

export const DESTINATIONS_SEARCH_PARAM_KEYS = [
  ...WHERE_PARAM_KEYS,
  ...WHEN_PARAM_KEYS,
  BUDGET_PARAM,
  TEMP_PARAM,
  CLIMATE_PARAM,
  TIMEZONE_PARAM,
  LANGUAGES_PARAM,
  SORT_PARAM,
  ...LEGACY_SEARCH_PARAM_KEYS,
] as const;

// Köppen main classes ride as the words the filter UI shows (its i18n labels),
// not the raw A–E letters.
const CLIMATE_WORDS = ["tropical", "dry", "temperate", "continental", "polar"] as const;
type ClimateWord = (typeof CLIMATE_WORDS)[number];
const CLIMATE_TO_KOPPEN: Record<ClimateWord, KoppenMainClass> = {
  tropical: "A",
  dry: "B",
  temperate: "C",
  continental: "D",
  polar: "E",
};
const KOPPEN_TO_CLIMATE: Record<KoppenMainClass, ClimateWord> = {
  A: "tropical",
  B: "dry",
  C: "temperate",
  D: "continental",
  E: "polar",
};

const SORT_TO_PARAM: Record<string, string> = {
  destinations_price_asc: "price_asc",
  destinations_price_desc: "price_desc",
};
const PARAM_TO_SORT: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_TO_PARAM).map(([sort, param]) => [param, sort]),
);

function parseOffsets(raw: string | null): UtcOffset[] {
  if (!raw) return [];
  const valid = new Set<number>(UTC_OFFSETS);
  const offsets = raw
    .split(",")
    .map(Number)
    .filter((n): n is UtcOffset => valid.has(n));
  return [...new Set(offsets)].sort((a, b) => a - b);
}

// Outside the slider's range means the link did not come from the slider, so
// there is no budget to honour; null leaves the ranking to anchor on the corpus.
// A bare integer is the pre-range grammar (`?budget=450000`, a ceiling) and
// still resolves so old shared links keep their meaning.
function parseBudget(raw: string | null): [number, number] | null {
  if (!raw) return null;
  const match = /^(\d+)(?:-(\d+))?$/.exec(raw);
  if (!match?.[1]) return null;
  const min = match[2] === undefined ? BUDGET_MIN_CENTS : Number(match[1]);
  const max = match[2] === undefined ? Number(match[1]) : Number(match[2]);
  if (min < BUDGET_MIN_CENTS || max > BUDGET_MAX_CENTS || min > max) return null;
  if (min === BUDGET_MIN_CENTS && max === BUDGET_MAX_CENTS) return null;
  return [min, max];
}

export interface DestinationsFilterParams {
  budget: [number, number] | null;
  selectedBands: TemperatureBand[];
  selectedKoppen: KoppenMainClass[];
  selectedOffsets: UtcOffset[];
  selectedLanguages: LanguageCode[];
}

export function parseDestinationsFilterParams(url: string): DestinationsFilterParams {
  const { params } = splitUrl(url);
  return {
    budget: parseBudget(params.get(BUDGET_PARAM)),
    selectedBands: parseCanonicalList(params.get(TEMP_PARAM), TEMPERATURE_BANDS),
    selectedKoppen: parseCanonicalList(params.get(CLIMATE_PARAM), CLIMATE_WORDS).map((word) => CLIMATE_TO_KOPPEN[word]),
    selectedOffsets: parseOffsets(params.get(TIMEZONE_PARAM)),
    selectedLanguages: parseCanonicalList(params.get(LANGUAGES_PARAM), LANGUAGE_OPTIONS),
  };
}

export function serializeDestinationsFilterParams(
  filters: DestinationsFilterParams,
): Record<string, string | undefined> {
  const climateWords = filters.selectedKoppen.map((koppen) => KOPPEN_TO_CLIMATE[koppen]);
  return {
    [BUDGET_PARAM]: filters.budget === null ? undefined : `${filters.budget[0]}-${filters.budget[1]}`,
    [TEMP_PARAM]: serializeCanonicalList(filters.selectedBands, TEMPERATURE_BANDS),
    [CLIMATE_PARAM]: serializeCanonicalList(climateWords, CLIMATE_WORDS),
    [TIMEZONE_PARAM]:
      filters.selectedOffsets.length > 0 ? [...filters.selectedOffsets].sort((a, b) => a - b).join(",") : undefined,
    [LANGUAGES_PARAM]: serializeCanonicalList(filters.selectedLanguages, LANGUAGE_OPTIONS),
  };
}

/** The URL's sort as `initialUiState` (sortBy only — the filter lists are local
 *  state; seeding them here too would double-toggle against the
 *  DestinationsFiltersRefinements bridge). `undefined` when absent. */
export function toDestinationsUiState(url: string, indexName: string): UiState | undefined {
  const sort = PARAM_TO_SORT[splitUrl(url).params.get(SORT_PARAM) ?? ""];
  if (!sort) return undefined;
  return { [indexName]: { sortBy: sort } };
}

export function serializeDestinationsSort(currentValue: string): string | undefined {
  return SORT_TO_PARAM[currentValue];
}

/** Whether the URL carries any destinations-owned search param (v2 or legacy). */
export function hasDestinationsSearchParams(url: string): boolean {
  const { params } = splitUrl(url);
  return DESTINATIONS_SEARCH_PARAM_KEYS.some((key) => params.has(key));
}
